// Angular DOM
import { Component, ViewChild, TemplateRef, OnDestroy } from "@angular/core";
// Angular Hooks
import { OnInit, AfterViewInit } from "@angular/core";
// Redux Store
import { Store } from "@ngrx/store";
import { SetPatientAction } from "../../actions";
// Models
import { Patient } from "../../models";
// Third Party Services
import { ActivatedRoute, Router, NavigationExtras } from "@angular/router";
import { NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
// Services
import {AuthService, PatientService, UtilService} from "../../services";
import { Location } from "@angular/common";
import { PaymentTypes, System } from "app/shared/models/system.model";
import { SystemService } from "app/shared/services/system/system.service";
import { Subscription } from "rxjs/Subscription";
import * as ENV from "../../../../environments/environment";

@Component({
  selector: "app-patient-billing-modal",
  templateUrl: "./patient-billing-modal.component.html",
  styleUrls: ["./patient-billing-modal.component.scss"],
})
export class PatientBillingEditModalComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("content") template: TemplateRef<any>;
  public date = new Date(); // today
  public hipaaCheckBox:boolean;
  public hippaFormUrl: string;
  public hippaFormId: string;
  public patient: Patient;
  public modalRef: any;
  public confirm: boolean = false;
  public editMode: boolean = true;
  public subscription: Subscription[] = [];
  public modalTitle: string = "Edit Patient Billing";
  public paymentOptions: string[] = [
    "Insurance",
    "Patient Self Pay",
    "Client Bill",
  ];
  public apiPaymentTypes: PaymentTypes = {
    InsuranceBillable: "0",
    ClientBillable: "0",
    SelfPayable: "0",
  };

  private isAdminEdit: boolean = false;
  private patientId: number;
  public systemName: string;
  public consentToDraw = true;
  public currentSystem: System;

  // exit edit/* workflow
  private cancelPath: string = "../../";

  public backButtonText = "CANCEL";
  public requestPending: boolean = false;

  private modalOptions: NgbModalOptions = {
    backdrop: "static",
    windowClass: "patient-billing",
    container: "app-patient-billing-modal",
    keyboard: false,
  };

  constructor(
    private authService: AuthService,
    public router: Router,
    public route: ActivatedRoute,
    private modalService: NgbModal,
    private patientService: PatientService,
    private store: Store<any>,
    private utilService: UtilService,
    private location: Location,
    private systemService: SystemService
  ) { }

  ngOnInit() {
    let system_id;
    this.route.queryParams.first().subscribe((params) => {
      this.patientId = +params["id"];
    });

    this.subscription["patient"] =  this.store.select("patient").subscribe((patient) => {
      this.patient = Object.assign({}, new Patient(), patient);
    });


    this.route.queryParams.first().subscribe((data) => {
      if (data.isAdminEdit) {
        this.isAdminEdit = data.isAdminEdit;
        this.backButtonText = "BACK";

        // admin-portal does not have a /patients/:id path, only a /patients.  let's trim off that :id
        this.cancelPath = "../../../";
      }
    });

    // fix empty InsurancePolicies array
    if (!this.patient.InsurancePolicies.length) {
      this.patient.InsurancePolicies[0] = { BillTo: "N/A" };
    }


    this.store.select("userSession").subscribe((userSession) => {
      system_id = userSession.Facility.System.Id;
      this.systemName = userSession.Facility.System.Name;
    });

    this.subscription["getSystemsById"] = this.systemService
      .getSystemById(system_id)
      .subscribe((response) => {
        const hippa = response.Attachments.filter(e=>e.TagList.find(e=>e == "hipaa")!== undefined);
        this.hippaFormUrl = hippa.length > 0 ? hippa[0].FileUrl : "";
        this.hippaFormId = hippa.length > 0 ? hippa[0].Id : "";
        this.apiPaymentTypes = response.PaymentTypes;
      });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.modalRef = this.modalService.open(this.template, this.modalOptions);
    });
  }

  ngOnDestroy() {
    this.subscription.forEach((sub) => {
      sub.unsubscribe();
    });
    setTimeout(() => {
      this.modalRef.close();
    });
  }

  onFacilitySelect(event, elem) {
    this.patient.BillToText = event;
    this.patient.InsurancePolicies[0].BillTo = "";
    elem.focus();
  }

  clearFacilityField() {
    this.patient.BillToText = "";
  }

  onFacilityRadioButton(elem) {
    this.patient.InsurancePolicies[0].BillTo = "";
    elem.focus();
  }

  setConfirm() {
    this.confirm = false;
  }

  canSkip() {
    return !(
      this.editMode ||
      this.patient.BillToText ||
      this.patient.InsurancePolicies[0].BillTo ||
      this.patient.InsurancePolicies[0].Provider ||
      this.patient.InsurancePolicies[0].GroupNumber ||
      this.patient.InsurancePolicies[0].PlanNumber
    );
  }

  isConsentToDrawChecked() {
    return this.consentToDraw;
  }

  checkFormValidity(form) {
    return form.invalid;
  }

  back() {
    if (this.isAdminEdit) {
      this.location.back();
    } else {
      this.cancel();
    }
  }

  onSelectAccountClientBill() {
    if (this.apiPaymentTypes && this.apiPaymentTypes.ClientBillable === "1") {
      this.patient.BillToText = this.systemName + " " + "Client Bill";
      this.patient.InsurancePolicies[0].BillTo = this.patient.BillToText;
    }
  }

  update() {
    this.requestPending = true;
    // Cleint Bill
    if (this.patient.InsurancePolicies[0].BillTo.includes(this.paymentOptions[2])) {
      this.patient.InsurancePolicies[0].Name = this.patient.BillToText;
      // Insurance
    } else if (
      this.patient.InsurancePolicies[0].BillTo === this.paymentOptions[0]
    ) {
      this.patient.InsurancePolicies[0].Name = this.patient.InsurancePolicies[0].Provider;

      // Patient Self Pay
    } else if (
      this.patient.InsurancePolicies[0].BillTo === this.paymentOptions[1]
    ) {
      this.patient.InsurancePolicies[0].Name = this.paymentOptions[1];
    }

    // if hipaa checkbox is checked
    // if(this.hipaaCheckBox)
    // {
    //   this.patient.HipaaFormId = this.hippaFormId;
    // }

    this.patientService.updatePatientBilling(this.patient).subscribe(() => {
      this.requestPending = false;
      this.store.dispatch(new SetPatientAction(this.patient, this.utilService));
      this.route.queryParams.first().subscribe((params) => {
        if (params["redirect"]) {

          let navigationExtras: NavigationExtras = {
            queryParams: { fromStore: true },
          };
          this.router.navigate([params["redirect"]], navigationExtras);
        } else {
          this.cancel();
        }
      });
    });
  }

  cancel() {
    this.router.navigate([this.cancelPath], { relativeTo: this.route });
  }


}



// WEBPACK FOOTER //
// ./src/app/shared/components/patient-billing-modal/patient-billing-edit-modal.component.ts